import { useState, type FormEvent } from "react";
import { api, ApiHatasi, mb, sayi } from "../api";
import { Bildirim, Durum, DurumRozeti, HataKutusu, Kart, Olcu, Tablo, useCek } from "../ui";
import { Kurulum } from "./Kurulum";

/**
 * KUTUM — posta kutusu sahibinin tek ekrani.
 * Burada yalnizca kendi kutusu vardir; baska bir kutuya gecis yolu yoktur.
 */
export function Kutum() {
  const kutum = useCek(() => api.kutum(), []);
  const [hata, setHata] = useState<ApiHatasi | null>(null);
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [bekliyor, setBekliyor] = useState<string | null>(null);

  async function webmailAc() {
    setHata(null);
    setBekliyor("webmail");
    try {
      const y = await api.kutumWebmail();
      // Bilet 60 saniyelik ve tek kullanimlik; yeni sekmede acilir
      window.open(y.url, "_blank", "noopener");
      setBilgi("Webmail yeni sekmede açıldı.");
    } catch (h) {
      setHata(h instanceof ApiHatasi ? h : new ApiHatasi(String(h), "bilinmiyor", 0));
    } finally {
      setBekliyor(null);
    }
  }

  return (
    <Durum cekim={kutum}>
      {(d) => (
        <>
          <div className="baslik">
            <div>
              <h1>{d.veri.eposta}</h1>
              <p>
                {d.veri.kurum ? d.veri.kurum + " · " : ""}
                {d.veri.aciklama ?? "Posta kutunuz"}
              </p>
            </div>
            <div className="eylemler">
              <DurumRozeti durum={d.veri.durum} />
              <button className="dugme ana" onClick={webmailAc} disabled={bekliyor !== null}>
                {bekliyor === "webmail" ? "Açılıyor…" : "Webmail'i aç"}
              </button>
            </div>
          </div>

          {hata && <HataKutusu hata={hata} />}
          {bilgi && <Bildirim tur="iyi">{bilgi}</Bildirim>}

          {d.veri.durum !== "aktif" && (
            <Bildirim tur="uyari">
              {d.veri.sebep ?? "Kutunuz geçici olarak kapalı."} Giriş ve gönderim kapalı;{" "}
              <strong>gelen postalarınız kaybolmaz</strong>, kutunuza düşmeye devam eder.
            </Bildirim>
          )}

          <div className="izgara d3" style={{ marginBottom: 18 }}>
            <Olcu ad="Kullanılan alan" deger={mb(d.veri.diskBayt)} />
            <Olcu
              ad="Kurumunuzun bugünkü gönderimi"
              deger={sayi(d.veri.kurumGunlukGonderim.kullanilan)}
              birim={"/ " + sayi(d.veri.kurumGunlukGonderim.limit)}
              oran={d.veri.kurumGunlukGonderim.kullanilan / d.veri.kurumGunlukGonderim.limit}
              not="kurumunuzun tamamı için"
            />
            <Olcu ad="Takma adlarınız" deger={d.veri.takmaAdlar.length} />
          </div>

          <div className="izgara d2">
            <Kart baslik="Parolanızı değiştirin">
              <ParolaFormu
                bitti={(m) => {
                  setBilgi(m);
                  setHata(null);
                }}
                hataVer={setHata}
              />
            </Kart>

            <Kart baslik="Telefon ve bilgisayar ayarları">
              <div className="ic">
                <p style={{ color: "var(--n-600)", marginTop: 0, fontSize: 13 }}>
                  Outlook, Thunderbird ya da telefonunuzun posta uygulamasında bu bilgileri kullanın.
                </p>
                <Tablo basliklar={["Ayar", "Değer"]}>
                  <tr>
                    <td>Kullanıcı adı</td>
                    <td className="tek">{d.ayarlar.kullaniciAdi}</td>
                  </tr>
                  <tr>
                    <td>Gelen posta (IMAP)</td>
                    <td className="tek">
                      {d.ayarlar.imap.sunucu} · {d.ayarlar.imap.port} · {d.ayarlar.imap.guvenlik}
                    </td>
                  </tr>
                  <tr>
                    <td>Giden posta (SMTP)</td>
                    <td className="tek">
                      {d.ayarlar.smtp.sunucu} · {d.ayarlar.smtp.port} · {d.ayarlar.smtp.guvenlik}
                    </td>
                  </tr>
                  <tr>
                    <td>Parola</td>
                    <td style={{ color: "var(--n-600)" }}>posta kutunuzun parolası</td>
                  </tr>
                </Tablo>
              </div>
            </Kart>
          </div>

          {d.veri.takmaAdlar.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Kart baslik="Size gelen diğer adresler">
                <div className="ic">
                  <p style={{ color: "var(--n-600)", marginTop: 0, fontSize: 13 }}>
                    Bu adreslere gelen postalar da bu kutuya düşer.
                  </p>
                  <Tablo basliklar={["Adres"]}>
                    {d.veri.takmaAdlar.map((a) => (
                      <tr key={a}>
                        <td className="tek">{a}</td>
                      </tr>
                    ))}
                  </Tablo>
                </div>
              </Kart>
            </div>
          )}

          {/* Kutu sahibinin menusu yoktur; kurulum rehberi buraya gomulur.
              Acilabilir tutuluyor: her giriste uzun bir rehber gormek yorar. */}
          <details className="rehber">
            <summary>Telefonuma ve bilgisayarıma nasıl kurarım?</summary>
            <div style={{ marginTop: 12 }}>
              <Kurulum />
            </div>
          </details>
        </>
      )}
    </Durum>
  );
}

function ParolaFormu({
  bitti,
  hataVer,
}: {
  bitti: (mesaj: string) => void;
  hataVer: (h: ApiHatasi | null) => void;
}) {
  const [eski, setEski] = useState("");
  const [yeni, setYeni] = useState("");
  const [tekrar, setTekrar] = useState("");
  const [bekliyor, setBekliyor] = useState(false);
  const [yerelHata, setYerelHata] = useState<string | null>(null);

  async function gonder(e: FormEvent) {
    e.preventDefault();
    setYerelHata(null);
    hataVer(null);
    if (yeni !== tekrar) {
      setYerelHata("Yeni parolalar birbirini tutmuyor.");
      return;
    }
    if (yeni.length < 10) {
      setYerelHata("Yeni parola en az 10 karakter olmalı.");
      return;
    }
    setBekliyor(true);
    try {
      const y = await api.kutumParola(eski, yeni);
      setEski("");
      setYeni("");
      setTekrar("");
      bitti(y.mesaj);
    } catch (h) {
      hataVer(h instanceof ApiHatasi ? h : new ApiHatasi(String(h), "bilinmiyor", 0));
    } finally {
      setBekliyor(false);
    }
  }

  return (
    <form className="ic" onSubmit={gonder}>
      {yerelHata && <Bildirim tur="kotu">{yerelHata}</Bildirim>}
      <label className="alan">
        <span>Mevcut parolanız</span>
        <input type="password" value={eski} onChange={(e) => setEski(e.target.value)} required autoComplete="current-password" />
      </label>
      <label className="alan">
        <span>Yeni parola</span>
        <input type="password" value={yeni} onChange={(e) => setYeni(e.target.value)} required autoComplete="new-password" />
        <small>En az 10 karakter. Başka yerde kullandığınız bir parolayı seçmeyin.</small>
      </label>
      <label className="alan">
        <span>Yeni parola (tekrar)</span>
        <input type="password" value={tekrar} onChange={(e) => setTekrar(e.target.value)} required autoComplete="new-password" />
      </label>
      <Bildirim>
        Parolanızı değiştirdikten sonra telefonunuzdaki ve bilgisayarınızdaki posta
        uygulamalarına da yeni parolayı girmeniz gerekir.
      </Bildirim>
      <button className="dugme ana" disabled={bekliyor}>
        {bekliyor ? "Değiştiriliyor…" : "Parolamı değiştir"}
      </button>
    </form>
  );

}
